import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatEuro, formatDate, formatTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, PhoneCall, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const SERVICES = [
  { code: "duplicate_key", name: "Faire un double de clé", desc: "Nouvelle clé quand vous en avez déjà une." },
  { code: "all_keys_lost", name: "Toutes les clés sont perdues", desc: "Reprogrammation complète nécessaire." },
  { code: "broken_key", name: "Clé cassée ou endommagée", desc: "Extraction / remplacement." },
  { code: "remote_repair", name: "Télécommande qui ne fonctionne plus", desc: "Batterie, boutons, réparation." },
  { code: "vehicle_locked", name: "Véhicule verrouillé", desc: "Ouverture sans dommages." },
  { code: "immobilizer", name: "Problème de démarrage / antidémarrage", desc: "Diagnostic sur place." },
  { code: "other", name: "Autre demande", desc: "Décrivez votre besoin." },
];

const QUESTIONS = {
  duplicate_key: [
    { key: "has_working_key", label: "Avez-vous encore une clé fonctionnelle ?", opts: ["yes", "no"] },
    { key: "has_remote", label: "Votre clé possède-t-elle des boutons (télécommande) ?", opts: ["yes", "no", "unknown"] },
  ],
  all_keys_lost: [
    { key: "vehicle_locked", label: "Le véhicule est-il actuellement verrouillé ?", opts: ["yes", "no"] },
    { key: "has_registration", label: "Avez-vous accès à la carte grise ?", opts: ["yes", "no"] },
  ],
  broken_key: [
    { key: "has_remote", label: "La clé cassée possède-t-elle des boutons ?", opts: ["yes", "no", "unknown"] },
  ],
  remote_repair: [
    { key: "buttons_response", label: "Les boutons répondent-ils partiellement ?", opts: ["yes", "no", "unknown"] },
  ],
  vehicle_locked: [
    { key: "keys_inside", label: "Les clés sont-elles dans le véhicule ?", opts: ["yes", "no"] },
  ],
  immobilizer: [],
  other: [],
};

const OPT_LABEL = { yes: "Oui", no: "Non", unknown: "Je ne sais pas" };

function ProgressBar({ step, total }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500">Étape {step} sur {total}</div>
      <div className="kf-progress-track mt-2">
        <div className="kf-progress-fill" style={{ width: `${(step / total) * 100}%` }}></div>
      </div>
    </div>
  );
}

export default function QuoteFlow() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [vehiclesData, setVehiclesData] = useState({ brands: {} });
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);

  const [vehicle, setVehicle] = useState({ brand: "", model: "", year: "", plate: "", vin: "" });
  const [serviceCode, setServiceCode] = useState("");
  const [answers, setAnswers] = useState({});
  const [location, setLocation] = useState({ postal_code: "", city: "", address: "" });
  const [urgency, setUrgency] = useState("standard");

  const [quote, setQuote] = useState(null); // priced or manual review
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customer, setCustomer] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "" });
  const [manualContact, setManualContact] = useState({ name: "", phone: "", email: "" });
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = 5;

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/tenants/${slug}`),
      axios.get(`${API}/public/vehicles`),
    ]).then(([t, v]) => {
      setTenant(t.data.tenant);
      setVehiclesData(v.data);
      document.documentElement.style.setProperty("--brand-primary", t.data.tenant.primary_color);
      document.documentElement.style.setProperty("--brand-accent", t.data.tenant.accent_color);
    }).catch(() => toast.error("Serrurier introuvable")).finally(() => setLoading(false));
  }, [slug]);

  const brands = useMemo(() => Object.keys(vehiclesData.brands || {}).sort(), [vehiclesData]);
  const models = useMemo(() => (vehiclesData.brands?.[vehicle.brand] || []), [vehiclesData, vehicle.brand]);
  const selectedModel = useMemo(() => models.find((x) => x.model === vehicle.model), [models, vehicle.model]);
  const years = useMemo(() => {
    const ranges = selectedModel?.year_ranges || [];
    const arr = [];
    for (const rng of ranges) {
      if (rng && rng[0] && rng[1]) { for (let y = rng[1]; y >= rng[0]; y--) arr.push(y); }
    }
    return [...new Set(arr)];
  }, [selectedModel]);
  const manualVehicle = vehicle.model === "__other__" || (!!vehicle.model && years.length === 0) || vehicle.yearManual === true;

  const questions = QUESTIONS[serviceCode] || [];

  function canAdvance() {
    if (step === 1) return vehicle.brand && vehicle.model && String(vehicle.year || "").length >= 4;
    if (step === 2) return !!serviceCode;
    if (step === 3) return questions.every((q) => answers[q.key]);
    if (step === 4) return location.postal_code.length >= 5 && location.city;
    return true;
  }

  async function submitQuote() {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/quotes`, {
        tenant_slug: slug,
        vehicle: { ...vehicle, model: vehicle.model === "__other__" ? "Autre modèle" : vehicle.model, year: Number(vehicle.year) || null, manual: manualVehicle },
        service_code: serviceCode,
        answers,
        location,
        urgency,
      });
      // Priceable request -> redirect to the secure customer quote page
      if (data.status === "priced" && data.access_token) {
        navigate(`/devis/${data.access_token}`, { replace: true });
        return;
      }
      // Manual-review path: stay in the flow to collect contact info
      setQuote(data);
      setStep(5);
    } catch (e) {
      toast.error("Impossible de calculer le devis");
    } finally { setSubmitting(false); }
  }

  async function submitBooking() {
    if (!selectedSlot) { toast.error("Choisissez un créneau"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/bookings`, {
        quote_id: quote.id,
        slot_start: selectedSlot,
        customer,
      });
      setConfirmation(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réservation impossible");
    } finally { setSubmitting(false); }
  }

  async function submitManualContact() {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/contact`, {
        quote_id: quote.id,
        ...manualContact,
      });
      setConfirmation({ manual: true });
    } catch (e) {
      toast.error("Impossible d'envoyer la demande");
    } finally { setSubmitting(false); }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!tenant) return <div className="min-h-screen flex items-center justify-center">Serrurier introuvable</div>;

  const primary = tenant.primary_color;
  const accent = tenant.accent_color;
  const brandStyle = { "--brand-primary": primary, "--brand-accent": accent };
  const isDemo = slug === "cle-express";

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${primary}0A 0%, #FBFCFE 40%)`, ...brandStyle }}>
      {/* Tenant header */}
      <header className="px-4 md:px-8 py-5 flex items-center justify-between max-w-3xl mx-auto">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-extrabold text-white" style={{ background: primary }}>
            {tenant.business_name.charAt(0)}
          </div>
          <div>
            <div className="font-display font-bold text-lg" style={{ color: primary }}>{tenant.business_name}</div>
            <div className="text-xs text-slate-500">Devis en ligne · propulsé par MonTempsGagné</div>
          </div>
        </Link>
        {tenant.phone && !isDemo && (
          <a href={`tel:${tenant.phone.replace(/\s/g, "")}`} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold" style={{color: primary}}>
            <PhoneCall className="w-4 h-4" /> {tenant.phone}
          </a>
        )}
      </header>

      <main className="px-4 md:px-8 py-4 pb-24 max-w-2xl mx-auto">
        {isDemo && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs font-semibold" style={{ borderColor: `${primary}55`, color: primary, background: `${primary}0A` }} data-testid="demo-env-banner">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }}></span>
            Environnement de démonstration
          </div>
        )}
        {step <= 4 && <ProgressBar step={step} total={totalSteps} />}

        {/* Step 1: Vehicle */}
        {step === 1 && (
          <section className="mt-6" data-testid="step-vehicle">
            <h1 className="font-display text-3xl font-extrabold" style={{color: primary}}>Quel est votre véhicule ?</h1>
            <p className="mt-2 text-slate-600">Nous avons besoin de la marque, du modèle et de l'année.</p>
            <div className="mt-6 grid gap-4">
              <div className="grid gap-1.5">
                <Label>Marque</Label>
                <Select value={vehicle.brand} onValueChange={(v) => setVehicle({ ...vehicle, brand: v, model: "", year: "", yearManual: false })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="vehicle-brand"><SelectValue placeholder="Choisir une marque" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Modèle</Label>
                <Select value={vehicle.model} onValueChange={(v) => setVehicle({ ...vehicle, model: v, year: "", yearManual: false })} disabled={!vehicle.brand}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="vehicle-model"><SelectValue placeholder={vehicle.brand ? "Choisir un modèle" : "Choisissez d'abord la marque"} /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {models.map((m) => <SelectItem key={m.model} value={m.model}>{m.model}</SelectItem>)}
                    <SelectItem value="__other__">Autre modèle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Année</Label>
                {manualVehicle ? (
                  <Input value={vehicle.year || ""} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} placeholder="Année (à confirmer)" className="h-12 rounded-xl" data-testid="vehicle-year-manual" disabled={!vehicle.model} />
                ) : (
                  <Select value={String(vehicle.year || "")} onValueChange={(v) => v === "__other__" ? setVehicle({ ...vehicle, year: "", yearManual: true }) : setVehicle({ ...vehicle, year: v })} disabled={!vehicle.model}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="vehicle-year"><SelectValue placeholder="Choisir une année" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      <SelectItem value="__other__">Autre année (à confirmer)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {manualVehicle && <p className="text-xs text-slate-500">Ce choix sera vérifié manuellement, sans devis automatique.</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Immatriculation <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                  <Input value={vehicle.plate} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value.toUpperCase() })} placeholder="AB-123-CD" className="h-12 rounded-xl" data-testid="vehicle-plate" />
                </div>
                <div className="grid gap-1.5">
                  <Label>VIN <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                  <Input value={vehicle.vin} onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase() })} placeholder="17 caractères" className="h-12 rounded-xl" data-testid="vehicle-vin" />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 2: Need */}
        {step === 2 && (
          <section className="mt-6" data-testid="step-need">
            <h1 className="font-display text-3xl font-extrabold" style={{color: primary}}>De quoi avez-vous besoin ?</h1>
            <p className="mt-2 text-slate-600">Sélectionnez la demande qui correspond le mieux.</p>
            <div className="mt-6 grid gap-3">
              {SERVICES.map((s) => (
                <div
                  key={s.code}
                  className="kf-select-card"
                  data-selected={serviceCode === s.code}
                  onClick={() => { setServiceCode(s.code); setAnswers({}); }}
                  data-testid={`service-${s.code}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-lg" style={{ color: primary }}>{s.name}</div>
                      <div className="text-sm text-slate-500 mt-0.5">{s.desc}</div>
                    </div>
                    {serviceCode === s.code && <Check className="w-5 h-5" style={{ color: accent }} />}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Step 3: Conditional questions */}
        {step === 3 && (
          <section className="mt-6" data-testid="step-questions">
            <h1 className="font-display text-3xl font-extrabold" style={{color: primary}}>Quelques précisions</h1>
            <p className="mt-2 text-slate-600">Pour vous proposer un devis fiable.</p>
            {questions.length === 0 && <p className="mt-4 text-slate-500 italic">Aucune question supplémentaire pour cette demande.</p>}
            <div className="mt-6 grid gap-6">
              {questions.map((q) => (
                <div key={q.key} data-testid={`question-${q.key}`}>
                  <div className="font-display font-bold text-lg" style={{color: primary}}>{q.label}</div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {q.opts.map((o) => (
                      <button
                        key={o}
                        type="button"
                        className="kf-select-card !py-3 !px-4 text-center font-semibold"
                        data-selected={answers[q.key] === o}
                        onClick={() => setAnswers({ ...answers, [q.key]: o })}
                        style={{ color: primary }}
                        data-testid={`answer-${q.key}-${o}`}
                      >
                        {OPT_LABEL[o]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <div className="font-display font-bold text-lg" style={{color: primary}}>Est-ce une demande urgente ?</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {["standard", "urgent"].map((o) => (
                    <button
                      key={o}
                      type="button"
                      className="kf-select-card !py-3 !px-4 text-center font-semibold"
                      data-selected={urgency === o}
                      onClick={() => setUrgency(o)}
                      style={{ color: primary }}
                      data-testid={`urgency-${o}`}
                    >
                      {o === "standard" ? "Sous quelques jours" : "Aujourd'hui / demain"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Step 4: Location */}
        {step === 4 && (
          <section className="mt-6" data-testid="step-location">
            <h1 className="font-display text-3xl font-extrabold" style={{color: primary}}>Où se trouve le véhicule ?</h1>
            <p className="mt-2 text-slate-600">Nous ajusterons le tarif selon la zone.</p>
            <div className="mt-6 grid gap-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5">
                  <Label>Code postal</Label>
                  <Input value={location.postal_code} onChange={(e) => setLocation({ ...location, postal_code: e.target.value })} placeholder="34000" maxLength={5} className="h-12 rounded-xl" data-testid="location-postal" />
                </div>
                <div className="col-span-2 grid gap-1.5">
                  <Label>Ville</Label>
                  <Input value={location.city} onChange={(e) => setLocation({ ...location, city: e.target.value })} placeholder="Montpellier" className="h-12 rounded-xl" data-testid="location-city" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Adresse <span className="text-slate-400 font-normal">(optionnel)</span></Label>
                <Input value={location.address} onChange={(e) => setLocation({ ...location, address: e.target.value })} placeholder="12 rue de la Paix" className="h-12 rounded-xl" data-testid="location-address" />
              </div>
            </div>
          </section>
        )}

        {/* Step 5: Result */}
        {step === 5 && quote && !confirmation && (
          <section className="mt-6" data-testid="step-result">
            {quote.status === "priced" ? (
              <>
                <div className="kf-card p-6 md:p-8">
                  <div className="text-xs font-bold uppercase tracking-widest" style={{color: accent}}>Votre devis est prêt</div>
                  <div className="mt-3 font-display text-2xl font-bold" style={{color: primary}}>
                    {vehicle.brand} {vehicle.model} · {vehicle.year}
                  </div>
                  <div className="text-slate-600">{quote.service_name} — {location.city}</div>
                  <div className="mt-6 font-display font-extrabold text-5xl" style={{color: primary}} data-testid="quote-price">
                    {formatEuro(quote.price)} <span className="text-lg font-bold text-slate-400">TTC</span>
                  </div>
                  {quote.quote_number && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500" data-testid="quote-meta">
                      <span>Devis N° <span className="font-mono font-semibold" style={{color: primary}}>{quote.quote_number}</span></span>
                      {quote.valid_until && <span>· Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString("fr-FR")}</span>}
                    </div>
                  )}
                  {quote.applied_rules?.length > 0 && (
                    <details className="mt-4 text-sm text-slate-600">
                      <summary className="cursor-pointer font-semibold">Détail du calcul</summary>
                      <ul className="mt-2 space-y-1 pl-4">
                        <li>Prix de base : {formatEuro(quote.base_price)}</li>
                        {quote.applied_rules.map((r, i) => (
                          <li key={i}>• {r.label} : +{formatEuro(r.adjustment)}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {quote.quote_number && (
                    <a
                      href={`${API}/public/quotes/${quote.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-2 rounded-full border-2 font-semibold px-5 py-2 text-sm transition-base"
                      style={{ borderColor: primary, color: primary }}
                      data-testid="customer-download-quote"
                    >
                      <Download className="w-4 h-4" /> Télécharger mon devis
                    </a>
                  )}
                </div>

                {/* Slots */}
                <div className="mt-8">
                  {isDemo && (
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dashed px-3 py-1 text-[11px] font-semibold" style={{ borderColor: `${primary}55`, color: primary }} data-testid="demo-env-booking">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }}></span> Environnement de démonstration
                    </div>
                  )}
                  <h2 className="font-display text-2xl font-bold" style={{color: primary}}>Choisissez votre créneau</h2>
                  <div className="mt-4 grid gap-4">
                    {Object.keys(slotsByDate).length === 0 && (
                      <div className="text-slate-500 text-sm">Aucun créneau disponible actuellement. Merci de contacter le serrurier.</div>
                    )}
                    {Object.entries(slotsByDate).slice(0, 7).map(([d, slots]) => (
                      <div key={d}>
                        <div className="font-semibold text-sm capitalize" style={{color: primary}}>
                          {new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {slots.map((s) => (
                            <button
                              key={s}
                              type="button"
                              className="kf-slot"
                              data-selected={selectedSlot === s}
                              onClick={() => setSelectedSlot(s)}
                              style={selectedSlot === s ? { background: accent, borderColor: accent, color: "#fff" } : {}}
                              data-testid={`slot-${s}`}
                            >
                              {formatTime(s)}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Customer */}
                <div className="mt-8 kf-card p-6">
                  <h3 className="font-display text-xl font-bold" style={{color: primary}}>Vos coordonnées</h3>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5"><Label>Prénom</Label><Input value={customer.first_name} onChange={(e) => setCustomer({ ...customer, first_name: e.target.value })} className="h-11 rounded-xl" data-testid="cust-first" /></div>
                    <div className="grid gap-1.5"><Label>Nom</Label><Input value={customer.last_name} onChange={(e) => setCustomer({ ...customer, last_name: e.target.value })} className="h-11 rounded-xl" data-testid="cust-last" /></div>
                    <div className="grid gap-1.5"><Label>Téléphone</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="h-11 rounded-xl" data-testid="cust-phone" /></div>
                    <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="h-11 rounded-xl" data-testid="cust-email" /></div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500" data-testid="quote-privacy-note">
                    Vos coordonnées sont transmises au professionnel pour traiter votre demande.{" "}
                    <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" style={{ color: primary }}>Confidentialité</a>.
                  </p>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={submitBooking}
                    disabled={submitting || !selectedSlot || !customer.first_name || !customer.last_name || !customer.phone || !customer.email}
                    className="rounded-full h-12 px-8 text-base font-semibold flex-1"
                    style={{ background: primary, color: "#fff" }}
                    data-testid="confirm-booking"
                  >
                    {submitting ? "Réservation…" : "Réserver cette intervention"}
                  </Button>
                  <Button variant="outline" onClick={() => { setStep(1); setQuote(null); }} className="rounded-full h-12" data-testid="modify-request">Modifier ma demande</Button>
                </div>
              </>
            ) : (
              <div className="kf-card p-6 md:p-8" data-testid="manual-review">
                <div className="text-xs font-bold uppercase tracking-widest" style={{color: accent}}>Vérification nécessaire</div>
                <h2 className="mt-3 font-display text-2xl font-bold" style={{color: primary}}>Votre demande nécessite une vérification</h2>
                <p className="mt-2 text-slate-600">Certaines demandes nécessitent une vérification rapide par le serrurier. Toutes les informations que vous avez renseignées lui seront transmises afin d'éviter de vous reposer les mêmes questions.</p>
                <div className="mt-6 grid gap-4">
                  <div className="grid gap-1.5"><Label>Nom complet</Label><Input value={manualContact.name} onChange={(e) => setManualContact({ ...manualContact, name: e.target.value })} className="h-11 rounded-xl" data-testid="manual-name" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5"><Label>Téléphone</Label><Input value={manualContact.phone} onChange={(e) => setManualContact({ ...manualContact, phone: e.target.value })} className="h-11 rounded-xl" data-testid="manual-phone" /></div>
                    <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={manualContact.email} onChange={(e) => setManualContact({ ...manualContact, email: e.target.value })} className="h-11 rounded-xl" data-testid="manual-email" /></div>
                  </div>
                  <Button
                    onClick={submitManualContact}
                    disabled={submitting || !manualContact.name || !manualContact.phone || !manualContact.email}
                    className="rounded-full h-12 font-semibold" style={{ background: primary, color: "#fff" }}
                    data-testid="submit-manual"
                  >
                    {submitting ? "Envoi…" : "Envoyer ma demande"}
                  </Button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Confirmation */}
        {confirmation && !confirmation.manual && (
          <section className="mt-6" data-testid="confirmation">
            <div className="kf-card p-6 md:p-8 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, color: accent }}>
                <Check className="w-7 h-7" />
              </div>
              <h1 className="mt-4 font-display text-3xl font-extrabold" style={{color: primary}}>Votre intervention est réservée</h1>
              <p className="mt-2 text-slate-600">Une confirmation détaillée vous sera envoyée par le serrurier.</p>

              <dl className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 text-left">
                <dt className="text-xs text-slate-500 uppercase font-semibold">Référence devis</dt><dd className="font-mono font-semibold text-sm" style={{color: primary}}>{confirmation.quote?.id?.slice(0,8).toUpperCase()}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Référence booking</dt><dd className="font-mono font-semibold text-sm" style={{color: primary}} data-testid="conf-ref">{confirmation.booking?.reference}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Véhicule</dt><dd className="font-semibold text-sm">{vehicle.brand} {vehicle.model} {vehicle.year}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Prestation</dt><dd className="font-semibold text-sm">{quote.service_name}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Prix</dt><dd className="font-display font-extrabold text-lg" style={{color: primary}}>{formatEuro(quote.price)}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Date</dt><dd className="font-semibold text-sm">{formatDate(confirmation.booking?.slot_start)}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Heure</dt><dd className="font-semibold text-sm">{formatTime(confirmation.booking?.slot_start)}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Lieu</dt><dd className="font-semibold text-sm">{location.city} {location.postal_code}</dd>
                <dt className="text-xs text-slate-500 uppercase font-semibold">Serrurier</dt><dd className="font-semibold text-sm">{tenant.business_name}</dd>
              </dl>
              <Link to="/" className="mt-8 inline-block text-sm font-semibold" style={{color: primary}}>← Retour à l'accueil</Link>
            </div>
          </section>
        )}

        {confirmation?.manual && (
          <section className="mt-6" data-testid="manual-confirmation">
            <div className="kf-card p-8 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, color: accent }}>
                <Check className="w-7 h-7" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-extrabold" style={{color: primary}}>Votre demande nécessite une vérification</h1>
              <p className="mt-3 text-slate-600">Ce cas ne permet pas de générer un tarif automatiquement. Vos informations ont été transmises au serrurier, qui dispose déjà de tous les éléments nécessaires pour vous répondre.</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{color: accent}}>
                <Check className="w-4 h-4" /> Pas besoin de refaire votre demande par téléphone.
              </div>
              <div className="mt-6"><Link to="/" className="inline-block text-sm font-semibold" style={{color: primary}}>← Retour à l'accueil</Link></div>
            </div>
          </section>
        )}

        {/* Nav buttons */}
        {step <= 4 && (
          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="rounded-full"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Précédent
            </Button>
            <Button
              onClick={() => (step === 4 ? submitQuote() : setStep(step + 1))}
              disabled={!canAdvance() || submitting}
              className="rounded-full h-12 px-8 font-semibold"
              style={{ background: primary, color: "#fff" }}
              data-testid="btn-next"
            >
              {step === 4 ? (submitting ? "Calcul…" : "Voir mon devis") : "Continuer"}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
